import { useCallback, useState } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import {
  type OrganizationDetails,
} from '@/features/auth/auth-model'
import { OnboardingLayout } from '@/features/onboarding/onboarding-layout'
import { EmailVerificationPage } from '@/features/onboarding/email-verification-page'
import { OrganizationDetailsPage } from '@/features/onboarding/organization-details-page'
import { VerificationPendingPage } from '@/features/onboarding/verification-pending-page'
import { useSession } from '@/shared/hooks/useSession'
import { useSaveOrganizationMandatoryInfo } from '@/shared/hooks/api/organizations'
import { toast } from 'sonner'

const emptyOrganizationDetails: OrganizationDetails = {
  placeOfRegistration: '',
  organizationName: '',
  localName: '',
}

export function OnboardingFlow() {
  const { session } = useSession()
  const { organization } = session

  const isMandatoryInfoComplete = organization.isMandatoryInfoComplete

  const [emailVerificationState, setEmailVerificationState] = useState({
    verificationCode: '',
  })

  const [organizationFormState, setOrganizationFormState] =
    useState<OrganizationDetails>(emptyOrganizationDetails)

  const navigate = useNavigate()

  const onEmailVerificationComplete = useCallback(
    (payload: { verificationCode: string }) => {
      setEmailVerificationState({
        verificationCode: payload.verificationCode,
      })
      navigate('/onboarding/organization-details', { replace: true })
    },
    [navigate]
  )

  const { mutateAsync: saveOrganizationMandatoryInfo } =
    useSaveOrganizationMandatoryInfo(session.organization.id)

  const onOrganizationDetailsComplete = useCallback(
    async (payload: OrganizationDetails) => {
      const newState: OrganizationDetails = {
        ...organizationFormState,
        ...payload,
      }
      setOrganizationFormState(newState)

      try {
        await saveOrganizationMandatoryInfo(newState)
        navigate('/onboarding/pending', { replace: true })
      } catch (error) {
        console.error(error)
        toast.error('Something went wrong. Please try again.')
      }
    },
    [organizationFormState, saveOrganizationMandatoryInfo, navigate]
  )

  const isDoneWithEmailVerification = session.isEmailVerified

  const isDoneWithOnboarding =
    isMandatoryInfoComplete && isDoneWithEmailVerification

  function getRedirectRoute() {
    if (isDoneWithOnboarding) {
      return 'pending'
    }

    if (isDoneWithEmailVerification) {
      return 'organization-details'
    }

    return 'email-verification'
  }

  const redirectRoute = getRedirectRoute()

  return (
    <Routes>
      <Route
        element={<OnboardingLayout />}
      >
        <Route
          path="email-verification"
          element={
            isDoneWithEmailVerification ? (
              <Navigate to={`/onboarding/${redirectRoute}`} replace />
            ) : (
              <EmailVerificationPage
                initialState={emailVerificationState}
                next={onEmailVerificationComplete}
              />
            )
          }
        />
        <Route
          path="organization-details"
          element={
            isDoneWithOnboarding ? (
              <Navigate to={`/onboarding/${redirectRoute}`} replace />
            ) : (
              <OrganizationDetailsPage
                next={onOrganizationDetailsComplete}
                initialState={organizationFormState}
              />
            )
          }
        />
        <Route path="pending" element={<VerificationPendingPage />} />
        <Route
          path="success"
          element={<Navigate to="/onboarding/pending" replace />}
        />
        <Route
          index
          element={<Navigate to={`/onboarding/${redirectRoute}`} replace />}
        />
      </Route>
    </Routes>
  )
}
